import { DAMAGE_CLASS_COLOR_MAP, abyssColors } from '@/theme/colors';
import {
  Accordion,
  Box,
  Button,
  Card,
  Dialog,
  Menu,
  Stack,
  alpha,
  styled as muiStyled,
} from '@mui/material';
import TextField from '@mui/material/TextField';

export const RiskCommentDialog = muiStyled(Dialog)(() => ({
  background: alpha('#000', 0.85),
  '& .MuiPaper-root': {
    maxWidth: '53.6rem',
    height: '41.8rem',
    borderRadius: 0,
    boxShadow: 'none',
  },
  '& .MuiDialogTitle-root': {
    padding: '3.1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '2rem',
    fontWeight: 600,
    color: abyssColors.primary[500],
    lineHeight: 'normal',
  },
  '& .MuiIconButton-root': {
    padding: 0,
  },
  '& .MuiDialogContent-root': {
    padding: '0.8rem 3.2rem 6rem 3.2rem',
    flex: 'none',
  },
  '& .MuiInputBase-root': {
    padding: '2rem',
    fontSize: '1.6rem',
    color: abyssColors.primary[500],
    fontWeight: 400,
    height: '20rem',
    alignItems: 'start',
  },
  '& fieldset': {
    borderColor: '#ddd',
  },
  '& .MuiDialogActions-root': {
    height: '6.2rem',
    padding: '1.6rem 3rem',
    gap: '2.4rem',
  },
  '& .MuiFormControl-root': {
    margin: 0,
  },
}));

type ButtonProps = {
  contained?: boolean;
  compact?: boolean;
};

export const ActionButton = muiStyled(Button)(({ contained, compact }: ButtonProps) => ({
  padding: compact ? '0.8rem 1.2rem' : '0.8rem 1.8rem',
  height: '3.4rem',
  borderRadius: '0.4rem',
  fontSize: '1.4rem',
  background: contained ? abyssColors.primary[500] : abyssColors.neutral[50],
  color: contained ? abyssColors.neutral[50] : abyssColors.primary[500],
  boxShadow: 'none',
  textTransform: 'none',
  width: 'fit-content',
  lineHeight: 'normal',
  marginLeft: '0px !important',
  gap: '0.6rem',
  border: contained ? 'none' : `1px solid ${abyssColors.primary[500]}`,
  '&:disabled': {
    border: contained ? 'none' : `1px solid ${abyssColors.primary[200]}`,
    background: contained ? abyssColors.neutral[300] : abyssColors.neutral[50],
    color: contained ? abyssColors.neutral[50] : abyssColors.primary[300],
  },
  '&:hover': {
    border: contained ? 'none' : `1px solid ${abyssColors.primary[300]}`,
    background: contained ? abyssColors.primary[400] : abyssColors.neutral[50],
  },
}));

export const DialogActionButton = muiStyled(ActionButton)(({ contained }: ButtonProps) => ({
  fontSize: '1.2rem',
  height: '3rem',
  fontWeight: contained ? 600 : 500,
  padding: contained ? '0rem 3rem' : '0rem 2.4rem',
  border: 'none',
  '&:hover': {
    border: 'none',
  },
}));

export const SummaryCard = muiStyled(Card)(() => ({
  padding: '16px',
  border: `1px solid ${abyssColors.primary[100]}`,
  borderRadius: '8px',
}));

export const MenuStyles = {
  marginTop: '12px',
  '& .MuiMenu-list': {
    padding: '8px',
    width: '100%',
    minWidth: '256px',
    borderRadius: '4px',
  },
  '& .MuiPopover-paper ': {
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.30)',
  },
};

export const getCriticalityLevelColor = (key: string) => {
  switch (key) {
    case 'N/A':
      return DAMAGE_CLASS_COLOR_MAP['N/A'];
    case 'DC0':
      return DAMAGE_CLASS_COLOR_MAP.DC0;
    case 'DC1':
      return DAMAGE_CLASS_COLOR_MAP.DC1;
    case 'DC2':
      return DAMAGE_CLASS_COLOR_MAP.DC2;
    case 'DC3':
      return DAMAGE_CLASS_COLOR_MAP.DC3;
    case 'DC4':
      return DAMAGE_CLASS_COLOR_MAP.DC4;
    case 'DC5':
      return DAMAGE_CLASS_COLOR_MAP.DC5;
    case 'RC1':
      return DAMAGE_CLASS_COLOR_MAP.RC1;
    case 'RC2':
      return DAMAGE_CLASS_COLOR_MAP.RC2;
    case 'RC3':
      return DAMAGE_CLASS_COLOR_MAP.RC3;
    case 'RC4':
      return DAMAGE_CLASS_COLOR_MAP.RC4;
    default:
      return abyssColors.neutral[100];
  }
};

type BarProps = {
  background: string;
  offset: number;
  width: string;
};

export const Bar = muiStyled(Box)(({ background, offset, width }: BarProps) => ({
  backgroundColor: background,
  height: '12px',
  width: width,
  display: 'inline-block',
  border: '1px solid rgba(0, 0, 0, 0.20)',
  borderRadius: '2px',
  position: 'absolute',
  left: `${offset * 100}%`,
}));

export const ChartAccordian = muiStyled(Accordion)(() => ({
  '&::before': {
    opacity: 0,
  },
  '& .MuiAccordionSummary-root': {
    margin: 0,
    minHeight: 'min-content',
  },
}));

export const SummaryContainer = muiStyled(Stack)(() => ({
  width: '100%',
  height: '100vh',
  overflowY: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
}));

export const RiskTextField = muiStyled(TextField)(() => ({
  '& .MuiInputBase-input': {
    color: abyssColors.primary[500],
  },
  '& .MuiFormLabel-root': {
    color: abyssColors.primary[300],
  },
  '& .MuiInputBase-formControl::before': {
    borderBottom: `1px solid ${abyssColors.primary[100]} !important`,
  },
  '& .MuiInputBase-formControl::after': {
    borderBottom: `1px solid ${abyssColors.primary[100]}`,
  },
}));

export const tooltipProps = {
  tooltip: {
    sx: {
      backgroundColor: 'rgba(0,0,0, 0.7)',
      color: 'white',
      opacity: 1,
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      padding: '1.2rem',
      height: '3.6rem',
      fontWeight: 500,
      borderRadius: '0.4rem',
      lineHeight: 'normal',
    },
  },
  arrow: {
    sx: { color: 'rgba(0,0,0, 0.7)' },
  },
};

export const StyledButtonMenu = muiStyled(Menu)(() => ({
  marginTop: '1rem',
  '& .MuiPaper-root': {
    minWidth: '24.6rem',
    borderRadius: 0,
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.30)',
  },
  '& .MuiMenuItem-root': {
    gap: '0.4rem',
    fontSize: '1.2rem',
    fontWeight: 400,
    alignItems: 'center',
    color: abyssColors.primary[500],
    '&:hover': {
      backgroundColor: abyssColors.neutral[50],
    },
  },
}));
